import { computed, inject, Injectable, signal } from '@angular/core';
import { PostResponse } from '../studio-http-client/post-response';
import { HttpAction } from '../studio-http-client/http-action.enum';
import { ApiService } from './api.service';
import { firstValueFrom, Observable } from 'rxjs';
import { NetworkInfoRequest, NetworkInfoType } from '../models/network-info-request';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { DbTables } from '../db/db-tables';
import { DbService } from '../db/db.service';
import { Store } from '@ngrx/store';
import * as AuthSelectors from '../store/auth/auth.selectors';
import { selectUserFullName } from '../store/user/user.selectors';
import { DateUtils } from '../../shared/utils/date.utils';
import { NetworkConnectionService } from './network-connection.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceService } from './device.service';
import { ENV_CONFIG, EnvironmentConfig } from '../../environment.config';

@Injectable({
  providedIn: 'root'
})
export class NetworkLoggingService {

  apiService = inject(ApiService);
  dbService = inject(DbService);
  store = inject(Store);
  networkConnection = inject(NetworkConnectionService);
  deviceService = inject(DeviceService);
  environmentConfig = inject<EnvironmentConfig>(ENV_CONFIG);

  districtId = this.store.selectSignal(AuthSelectors.selectLSDistrictId);
  schoolId = this.store.selectSignal(AuthSelectors.selectSchoolId);
  classId = this.store.selectSignal(AuthSelectors.selectCurrentClassId);
  userId = this.store.selectSignal(AuthSelectors.selectUserId);
  mpUsername = this.store.selectSignal(AuthSelectors.selectMpUsername);
  mpUserEmail = this.store.selectSignal(AuthSelectors.selectMpEmail);
  username = computed(() => this.mpUserEmail() && this.mpUserEmail() !== 'none' ? this.mpUserEmail() : this.mpUsername());
  isAuthenticated = this.store.selectSignal(AuthSelectors.selectIsAuthLoaded);
  userFullName = this.store.selectSignal(selectUserFullName);

  hasNetworkConnection = signal(this.networkConnection.connectionState.hasNetworkConnection);

  private totalFailureCount = 0;

  constructor() {
    this.networkConnection.networkChange$.pipe(takeUntilDestroyed()).subscribe(state => {
      this.hasNetworkConnection.set(state?.hasNetworkConnection);
    });
    this.scheduleProcessQueue();
  }

  async checkInternetAccess(): Promise<boolean> {
    try {
      const res = await fetch(`${environment.lsApiUrl}api/Ping/Unauthenticated`, {
        method: 'GET',
        cache: 'no-store',
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  async diagnoseError(url: string, error: HttpErrorResponse, duration: number) {
    if (url.includes('api/NetworkInfo')) {
      return;
    }

    // This will log it to sentry
    if (error.status === 504) {
      console.error('504 error: ', url, error, this.userId());
    }

    let type = 'Unknown Error';
    const isOnline = navigator.onLine;
    const status = error.status ?? 0;
    const message = error.message || 'No error message';
    let networkType: NetworkInfoType;
    if (!isOnline) {
      type = 'No Network Connection';
      networkType = NetworkInfoType.NoNetwork;
    } else if (status === 0) {
      const hasInternet = await this.checkInternetAccess();
      type = hasInternet
        ? 'Firewall or API Blocked (status=0, internet reachable)'
        : 'No Internet Access (connected to network but unreachable)';
      networkType = hasInternet ? NetworkInfoType.FirewallBlocked : NetworkInfoType.NoInternet;
    } else if (status >= 400 && status < 500) {
      return;
    } else if (status >= 500) {
      return;
    }

    const networkAvailable = isOnline && status !== 0;

    this.logNetworkInfo(networkAvailable, networkType, {
      type,
      url,
      status,
      message,
      duration,
      userAgent: navigator.userAgent
    });
  }

  logNetworkInfo(status: boolean, type: NetworkInfoType, additionalInfo: unknown) {
    // Only persist network ifo if user is authenticated and have acquired userId and classId
    if (this.isAuthenticated() && this.userId() && this.classId()) {
      return this.dbService.insert(DbTables.NetworkInfo, <NetworkInfoRequest>{
        type,
        districtId: this.districtId(),
        schoolId: this.schoolId(),
        classId: this.classId(),
        userId: this.userId(),
        username: this.username(),
        startTime: DateUtils.new().format(),
        endTime: DateUtils.new().add(1, 'minutes').format(),
        networkStatus: status,
        additionalInfo: JSON.stringify(additionalInfo),
        userFullName: this.userFullName(),
        deviceType: this.deviceService.getDeviceType(),
        userAgent: navigator.userAgent,
        lightSailVersion: this.environmentConfig.version,
      });
    }
  }

  /**
   * Sequence the processing
   */
  private scheduleProcessQueue(): void {
    this.processQueue()
      .then(result => {
        // If they ALL failed, increase totalFailureCount
        // (result is an array of booleans)
        if (Array.isArray(result) && result.length > 0) {
          if (result.includes(true)) {
            this.totalFailureCount = 0;
          } else {
            this.totalFailureCount++;
          }
        }
        // Exponential backoff relates to how many total failures we had, up to a maximum of 5 minutes
        // Base is 2 seconds
      })
      .finally(() =>
        setTimeout(
          () => this.scheduleProcessQueue(),
          Math.min(5 * 60 * 1000, 2000 * Math.pow(2, this.totalFailureCount))
        )
      );
  }

  /**
   * Set up the sending, and actually send the messages from the local queue
   */
  private async processQueue(): Promise<boolean[] | void> {
    // If we're not online or authenticated, we don't have a way of send event to server so bail
    if (!this.hasNetworkConnection() || !this.isAuthenticated()) {
      return;
    }

    const count = await this.dbService.getCount(DbTables.NetworkInfo);
    if (!count) {
      return [];
    }

    const events: NetworkInfoRequest[] = await this.dbService.getAll(DbTables.NetworkInfo);

    const results: boolean[] = [];
    for (const event of events) {
      try {
        await firstValueFrom(this.postNetworkIfo(event));
        await this.dbService.deleteByKey(DbTables.NetworkInfo, event.id);

        results.push(true);
      } catch (err: any) {

        // 400 and 500 error cause by either malformed request or payload, delete those corrupted records to avoid retrying them
        if (err.status === 400 || err.status === 500) {
          if(event?.id) {
            this.dbService.deleteByKey(DbTables.NetworkInfo, event.id);
          }
        }

        results.push(false);
      }
    }

    return results;
  }

  postNetworkIfo(request: NetworkInfoRequest): Observable<PostResponse> {
    const url = `api/NetworkInfo`;
    return this.apiService.invokeLSApi<PostResponse>(url, HttpAction.Post, request);
  }
}
